import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { Search, Download, FileText, DollarSign, Calendar } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { listInvoices, getInvoice, getInvoiceItems } from '../utils/db/invoices';
import { getProfile } from '../utils/db/profiles';
import InvoiceLayout from '../components/InvoiceLayout';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const DownloadInvoice = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [franchiseProfile, setFranchiseProfile] = useState(null);
  const printRef = useRef();
  const [pendingDownload, setPendingDownload] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: selectedInvoice ? `Invoice_${selectedInvoice.invoice_no}` : 'Invoice',
    removeAfterPrint: true
  });

  // Callback ref to ensure we print once the node is mounted
  const setPrintNode = (node) => {
    printRef.current = node;
    // No auto print here; we use this DOM for html2canvas -> jsPDF
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setIsLoading(true);
        const [rows, profile] = await Promise.all([
          listInvoices(user.id),
          getProfile(user.id)
        ]);
        setInvoices(rows || []);
        // Transform database profile to layout-friendly shape with terms as an array
        if (profile) {
          const transformedProfile = {
            franchiseName: profile.franchise_name || 'TATYS',
            address: profile.address || 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
            mobileNo: profile.mobile_no || '8015695652',
            email: profile.email || 'thirumazhisai.dist@gmail.com',
            gstNo: profile.gst_no || '27CIOPP1874N1Z',
            panNo: profile.pan_no || 'CIOPP1874N',
            bankName: profile.bank_name || 'Canara Bank',
            accountNo: profile.account_no || '102000820462',
            ifsc: profile.ifsc || 'CNRB0003072',
            branchName: profile.branch_name || 'THIRUMAZHISAI',
            invoicePrefix: profile.invoice_prefix || 'INV',
            logoUrl: profile.logo_url || null,
            terms: profile.terms ? profile.terms.split('\n') : [
              'Payment should be made by cheque in favour of TATYS',
              'Any queries must be forwarded within 15 days of receipt',
              '18% interest will be charged on overdue bills'
            ]
          };
          setFranchiseProfile(transformedProfile);
        } else {
          setFranchiseProfile({
            franchiseName: 'TATYS',
            address: 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
            mobileNo: '8015695652',
            email: 'thirumazhisai.dist@gmail.com',
            gstNo: '27CIOPP1874N1Z',
            panNo: 'CIOPP1874N',
            bankName: 'Canara Bank',
            accountNo: '102000820462',
            ifsc: 'CNRB0003072',
            branchName: 'THIRUMAZHISAI',
            invoicePrefix: 'INV',
            logoUrl: null,
            terms: [
              'Payment should be made by cheque in favour of TATYS',
              'Any queries must be forwarded within 15 days of receipt',
              '18% interest will be charged on overdue bills'
            ]
          });
        }
      } catch (e) {
        toast.error('Failed to load invoices');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user]);

  const filtered = invoices.filter(inv =>
    inv.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customers?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(inv.invoice_date || '').includes(searchTerm)
  );

  const totalRevenue = filtered.reduce((sum, inv) => sum + Number(inv.grand_total || 0), 0);

  const openAndDownload = async (invoice) => {
    try {
      if (downloadingId) return;
      setDownloadingId(invoice.id);
      setPendingDownload(true);
      const [full, items] = await Promise.all([
        getInvoice(user.id, invoice.id),
        getInvoiceItems(user.id, invoice.id)
      ]);
      setInvoiceItems(items || []);
      // Set invoice last to ensure layout sees items immediately
      setSelectedInvoice(full);
      // Wait for layout to mount in the offscreen container
      setTimeout(async () => {
        try {
          await generatePdfFromLayout();
        } catch (err) {
          toast.error('Failed to generate PDF');
        } finally {
          setSelectedInvoice(null);
          setPendingDownload(false);
          setDownloadingId(null);
        }
      }, 350);
    } catch (e) {
      toast.error('Failed to prepare invoice for download');
      setPendingDownload(false);
      setDownloadingId(null);
    } finally {
      // keep row-level loading via downloadingId
    }
  };

  // Generate PDF from the offscreen InvoiceLayout to match preview exactly
  const generatePdfFromLayout = async () => {
    const container = printRef.current;
    if (!container) throw new Error('Layout not ready');
    const page1 = container.querySelector('.page-break-after');
    const page2 = container.querySelector('.page-break-before');
    if (!page1 || !page2) throw new Error('Layout sections not found');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Page 1 (bill)
    const canvas1 = await html2canvas(page1, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      foreignObjectRendering: false
    });
    const a4Width = 210;
    const imgData1 = canvas1.toDataURL('image/jpeg', 0.8);
    const imgHeight1 = (canvas1.height * a4Width) / canvas1.width;
    pdf.addImage(imgData1, 'JPEG', 0, 0, a4Width, imgHeight1);

    // Page 2+ (bookings) - render each bookings page separately to preserve borders and pagination
    const bookingPages = container.querySelectorAll('.page-break-before');
    for (let i = 0; i < bookingPages.length; i++) {
      const el = bookingPages[i];
      const canvas = await html2canvas(el, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        foreignObjectRendering: false
      });
      const img = canvas.toDataURL('image/jpeg', 0.8);
      const imgHeight = (canvas.height * a4Width) / canvas.width;
      pdf.addPage();
      pdf.addImage(img, 'JPEG', 0, 0, a4Width, imgHeight);
    }

    pdf.save(`Invoice_${selectedInvoice?.invoice_no || 'Invoice'}.pdf`);
  };

  const calculateDays = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const calculateTotalWeight = (items) => {
    if (!items || !items.length) return '0.00';
    const sum = items.reduce((s, it) => s + (parseFloat(it.weight) || 0), 0);
    return sum.toFixed(2);
  };

  const formatItems = (items) => {
    if (!items || !items.length) return [];
    return items.map(item => ({
      date: item.date,
      cnote: item.awb_no,
      receiver: item.receiver_name,
      destination: item.receiver_address,
      mode: item.mode,
      weight: item.weight,
      qty: item.quantity,
      type: item.type,
      amount: Number(item.amount || 0).toFixed(2),
      other: Number(item.other_charge || 0).toFixed(2),
      total: (Number(item.amount || 0) + Number(item.other_charge || 0)).toFixed(2)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Download Invoices</h1>
          <p className="text-gray-600">Search and download invoices as PDF</p>
        </div>
      </div>

      {/* Search and stats */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by invoice no, customer, or date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center justify-end bg-green-50 text-green-700 px-4 py-2 rounded-md">
            <DollarSign className="h-5 w-5 mr-2" />
            <div>
              <span className="text-sm font-medium">Total Revenue:</span>
              <span className="ml-2 text-lg font-bold">₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map(inv => (
              <tr key={inv.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{inv.invoice_no}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{inv.customers?.customer_name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inv.invoice_date}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{inv.from_date} - {inv.to_date}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">₹{Number(inv.grand_total || 0).toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => openAndDownload(inv)}
                    disabled={!!downloadingId}
                    className="inline-flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                    title="Download PDF"
                  >
                    <Download className="h-4 w-4 mr-1" /> {downloadingId === inv.id ? 'Preparing...' : 'Download'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td className="px-6 py-4 text-center text-gray-500" colSpan="6">No invoices found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Page footer */}
      <footer className="mt-8 border-t border-gray-200">
        <div className="py-4 text-center text-xs text-gray-500">
          Powered by SoftNex
        </div>
      </footer>

      {/* Offscreen print layout (must be in DOM for react-to-print) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, height: 0, overflow: 'hidden' }}>
        {selectedInvoice && (
          <div ref={setPrintNode}>
            <InvoiceLayout
              invoice={{
                number: selectedInvoice.invoice_no || '',
                date: selectedInvoice.invoice_date || '',
                fromDate: selectedInvoice.from_date || '',
                toDate: selectedInvoice.to_date || '',
                customer: {
                  name: selectedInvoice.customers?.customer_name || '',
                  address: selectedInvoice.customers?.address || '',
                  gst: selectedInvoice.customers?.gst_no || ''
                },
                days: calculateDays(selectedInvoice.from_date || '', selectedInvoice.to_date || ''),
                consignment: Array.isArray(invoiceItems) ? invoiceItems.length : 0,
                weight: calculateTotalWeight(invoiceItems),
                // Base consignment amount (sum of item amounts, excluding additional charges)
                amount: (() => {
                  try {
                    const sum = (invoiceItems || []).reduce((s, it) => s + Number(it.amount || 0), 0);
                    return Number(sum).toFixed(2);
                  } catch { return '0.00'; }
                })(),
                items: formatItems(invoiceItems),
                subtotal: Number(selectedInvoice.subtotal || 0).toFixed(2),
                fuelPercent: Number(selectedInvoice.fuel_percent || 0).toFixed(2),
                fuelCharge: Number(selectedInvoice.fuel_charge || 0).toFixed(2),
                royaltyPercent: Number(selectedInvoice.royalty_percent || 0).toFixed(2),
                royaltyCharge: Number(selectedInvoice.royalty_charge || 0).toFixed(2),
                docketCharge: Number(selectedInvoice.docket_charge || 0).toFixed(2),
                odaCharge: Number(selectedInvoice.oda_charge || 0).toFixed(2),
                otherCharge: Number(selectedInvoice.other_charge || 0).toFixed(2),
                // GST based on customer state: 'same' => CGST+SGST, otherwise IGST
                ...(function () {
                  const isSameState = selectedInvoice.customers?.customer_state === 'same';
                  const subtotal = Number(selectedInvoice.subtotal || 0);
                  const userPercent = Number(selectedInvoice.gst_percent || 0);
                  if (isSameState) {
                    const cgstP = Number(selectedInvoice.cgst_percent != null ? selectedInvoice.cgst_percent : userPercent / 2);
                    const sgstP = Number(selectedInvoice.sgst_percent != null ? selectedInvoice.sgst_percent : userPercent / 2);
                    const cgstAmt = Number(selectedInvoice.cgst_amount != null ? selectedInvoice.cgst_amount : (subtotal * cgstP) / 100);
                    const sgstAmt = Number(selectedInvoice.sgst_amount != null ? selectedInvoice.sgst_amount : (subtotal * sgstP) / 100);
                    return {
                      gstType: 'CGST+SGST',
                      cgstPercent: cgstP.toFixed(0),
                      cgstAmount: cgstAmt.toFixed(2),
                      sgstPercent: sgstP.toFixed(0),
                      sgstAmount: sgstAmt.toFixed(2),
                      igstPercent: '0',
                      igstAmount: '0.00',
                      igst: (cgstAmt + sgstAmt).toFixed(2)
                    };
                  } else {
                    const igstP = Number(selectedInvoice.igst_percent != null ? selectedInvoice.igst_percent : userPercent);
                    const igstAmt = Number(selectedInvoice.igst_amount != null ? selectedInvoice.igst_amount : (subtotal * igstP) / 100);
                    return {
                      gstType: 'IGST',
                      cgstPercent: '0',
                      cgstAmount: '0.00',
                      sgstPercent: '0',
                      sgstAmount: '0.00',
                      igstPercent: igstP.toFixed(0),
                      igstAmount: igstAmt.toFixed(2),
                      igst: igstAmt.toFixed(2)
                    };
                  }
                })(),
                total: Number(selectedInvoice.grand_total || 0).toFixed(2)
              }}
              profile={franchiseProfile}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadInvoice;


